/**
 * TreatX Admin — Packages (PACKAGE-ADMIN-HYGIENE-1, 2026-05-21)
 *
 * Canonical single-surface admin entry for package management:
 *
 *   • Catalog               — sole product-grade package editor
 *                             (writes via canonical /api/packages writer).
 *   • Diagnostics            — internal · TreatX-super-admin only · read-only.
 *   • Cleanup Inventory      — internal · TreatX-super-admin only ·
 *                             dependency-aware inspection + safe-action
 *                             invocation of the canonical writer.
 *                             NEVER a package-definition editor.
 *
 * Strict-mode invariants enforced here (locked by drift guards):
 *   - Only ONE editable package-definition surface exists (Catalog).
 *   - Diagnostics and Cleanup Inventory render no package-field inputs.
 *   - Diagnostics + Cleanup Inventory tabs render only when the active
 *     user role is `treatx_super_admin`.
 *   - All cleanup actions delegate to the existing canonical writer
 *     endpoints (`PUT /api/packages/{id}/toggle`,
 *     `DELETE /api/packages/{id}`). No raw JSON edit. No new lifecycle
 *     vocabulary. No second authority.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  ArrowLeft, Package, FileJson, Info, Wrench,
  RefreshCw, AlertTriangle, ShieldAlert, Loader2,
} from 'lucide-react';
import { usePackageCatalogOptional } from '../../context/PackageCatalogContext';
import ClinicCRMPackageManagementPage from './ClinicCRMPackageManagementPage';
import { authHeaders, maybeClearStaleToken, getUnifiedAuth } from '../../utils/unifiedAuth';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Roles allowed to see internal surfaces (Diagnostics + Cleanup Inventory).
// Strictly the canonical TreatX super admin — locked by drift guard.
const SUPER_ADMIN_ROLES = ['treatx_super_admin'];

// Classification → display copy. Pure presentation; no business logic.
// OWNER-HYGIENE-1 (2026-05-21): classifications are mutually exclusive and
// operator-actionable. The fixture-name signal is shown separately as a
// badge so the wording cannot contradict itself (e.g. previous "Retained
// — fixture" + "safe to delete after operator confirmation").
const CLASSIFICATION_BADGE = {
  SAFE_TO_DELETE: { label: 'Safe to delete', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  RETAINED_FOR_DEPENDENCY: { label: 'Cannot delete — dependency', cls: 'bg-red-100 text-red-700 border-red-200' },
  RETAINED_FOR_HISTORY: { label: 'Cannot delete — history', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  LEGACY_SCHEMA_DRIFT: { label: 'Repair required — schema drift', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
};

// Lightweight error boundary so a crash in one tab can never unmount
// the merged-page shell.
class TabErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    console.error('[PackagesAdminMerged] embedded tab error:', err, info);
  }
  render() {
    if (this.state.err) {
      return (
        <div
          className="border border-amber-200 bg-amber-50 text-amber-800 rounded-md p-3 text-sm"
          data-testid="packages-tab-error"
        >
          This panel failed to render. Refresh or switch tabs. Details logged to console.
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Diagnostics tab (internal, read-only, super-admin-only) ───────────────
const DiagnosticsPanel = ({ allPackages, lastSync }) => (
  <div className="space-y-3" data-testid="packages-diagnostics-panel">
    <div
      className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
      data-testid="packages-diagnostics-notice"
    >
      <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-500" />
      <span>
        This surface is internal diagnostics and does not define package truth.
        Read-only. Mutations are not permitted here.
      </span>
    </div>

    <Card data-testid="packages-diagnostics-snapshot-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <FileJson className="h-3.5 w-3.5" /> Catalog snapshot
        </CardTitle>
      </CardHeader>
      <CardContent className="text-xs space-y-1">
        <div className="flex justify-between">
          <span className="text-slate-500">Last catalog sync</span>
          <span className="font-mono">
            {lastSync ? new Date(lastSync).toISOString() : '—'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">Total packages</span>
          <span className="font-mono">{allPackages.length}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">Active &amp; public</span>
          <span className="font-mono">
            {allPackages.filter((p) => p.active && p.public).length}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">Inactive</span>
          <span className="font-mono">
            {allPackages.filter((p) => !p.active).length}
          </span>
        </div>
      </CardContent>
    </Card>

    <Card data-testid="packages-diagnostics-raw-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <FileJson className="h-3.5 w-3.5" /> Raw catalog JSON (read-only)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <pre
          className="bg-slate-900 text-slate-50 text-[10px] leading-relaxed p-3 rounded-md overflow-auto max-h-[420px]"
          data-testid="packages-diagnostics-raw-json"
        >
          {JSON.stringify(allPackages, null, 2)}
        </pre>
      </CardContent>
    </Card>
  </div>
);

// ─── Cleanup Inventory tab (internal, super-admin-only, dependency-aware) ──
const CleanupInventoryPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name }

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API}/api/admin/packages/cleanup-inventory`,
        { headers: authHeaders() },
      );
      if (maybeClearStaleToken(res)) return;
      if (!res.ok) {
        setError(
          res.status === 403
            ? 'Forbidden. Only TreatX super admins can view the cleanup inventory.'
            : `Failed to load inventory (HTTP ${res.status}).`,
        );
        setData(null);
        return;
      }
      const body = await res.json();
      setData(body);
    } catch (e) {
      setError(`Network error: ${e.message || 'failed to reach backend'}.`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  // Deactivate via the existing canonical writer.
  const handleDeactivate = async (item) => {
    setBusyId(item.id);
    try {
      const res = await fetch(
        `${API}/api/packages/${encodeURIComponent(item.id)}/toggle`,
        { method: 'PUT', headers: authHeaders() },
      );
      if (maybeClearStaleToken(res)) return;
      // After mutation, re-pull dependency analysis.
      await fetchInventory();
    } catch (e) {
      setError(`Deactivate failed: ${e.message || 'network error'}.`);
    } finally {
      setBusyId(null);
    }
  };

  // Safe-delete via the existing canonical writer. Backend will still
  // refuse with 409 if dependencies appeared between read and write —
  // we surface that to the operator.
  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const item = confirmDelete;
    setConfirmDelete(null);
    setBusyId(item.id);
    try {
      const res = await fetch(
        `${API}/api/packages/${encodeURIComponent(item.id)}`,
        { method: 'DELETE', headers: authHeaders() },
      );
      if (maybeClearStaleToken(res)) return;
      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        const refs = body?.error_message || body?.detail?.blocking_refs || body?.message;
        setError(
          `Delete refused by canonical writer (dependencies appeared): ${
            typeof refs === 'string' ? refs : JSON.stringify(refs || {})
          }`,
        );
      } else if (!res.ok) {
        setError(`Delete failed (HTTP ${res.status}).`);
      }
      await fetchInventory();
    } catch (e) {
      setError(`Delete failed: ${e.message || 'network error'}.`);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-slate-400" data-testid="cleanup-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="packages-cleanup-inventory-panel">
      <div
        className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
        data-testid="cleanup-internal-banner"
      >
        <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
        <span>
          INTERNAL CLEANUP / NON-PRODUCTION. This surface is for operational
          hygiene only — it is not a package-definition editor and not
          owner-facing. All actions delegate to the canonical package
          writer endpoints.
        </span>
      </div>

      {error && (
        <div
          className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
          data-testid="cleanup-error"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Wrench className="h-3.5 w-3.5" /> Inactive / fixture-named packages
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchInventory}
              data-testid="cleanup-refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {data?.summary && (
            <div className="flex flex-wrap gap-2 text-xs mb-3" data-testid="cleanup-summary">
              <Badge variant="outline">Total: {data.summary.total}</Badge>
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                Safe to delete: {data.summary.safe_to_delete}
              </Badge>
              <Badge className="bg-red-100 text-red-700 border-red-200">
                Cannot delete — dependency: {data.summary.retained_for_dependency}
              </Badge>
              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                Cannot delete — history: {data.summary.retained_for_history}
              </Badge>
              <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                Repair required — schema drift: {data.summary.legacy_schema_drift ?? 0}
              </Badge>
            </div>
          )}

          {!data?.items?.length ? (
            <p className="text-xs text-slate-500 py-6 text-center" data-testid="cleanup-empty">
              No inactive or fixture-named packages.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="cleanup-table">
                <thead>
                  <tr className="text-slate-500 border-b">
                    <th className="text-left py-2 pr-3">Name</th>
                    <th className="text-left py-2 pr-3">Key</th>
                    <th className="text-left py-2 pr-3">Status</th>
                    <th className="text-left py-2 pr-3">Classification</th>
                    <th className="text-left py-2 pr-3">Dependencies</th>
                    <th className="text-left py-2 pr-3">Reason</th>
                    <th className="text-right py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => {
                    const badge = CLASSIFICATION_BADGE[item.classification] || CLASSIFICATION_BADGE.LEGACY_SCHEMA_DRIFT;
                    const refs = item.dependencies || {};
                    return (
                      <tr
                        key={item.id}
                        className="border-b last:border-0 align-top"
                        data-testid={`cleanup-row-${item.id}`}
                      >
                        <td className="py-2 pr-3 font-medium text-slate-800">
                          {item.name_en || '—'}
                          {item.is_fixture_named && (
                            <Badge
                              className="ml-1.5 text-[9px] bg-slate-100 text-slate-600 border-slate-200 align-middle"
                              data-testid={`cleanup-fixture-flag-${item.id}`}
                              title="Fixture-named row (UI hint; does not affect classification)"
                            >
                              fixture
                            </Badge>
                          )}
                        </td>
                        <td className="py-2 pr-3 font-mono text-[10px] text-slate-500">{item.package_key || item.id}</td>
                        <td className="py-2 pr-3">
                          <Badge variant="outline" className="text-[10px]" data-testid={`cleanup-status-${item.id}`}>
                            {item.status}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3">
                          <Badge className={`text-[10px] border ${badge.cls}`} data-testid={`cleanup-class-${item.id}`}>
                            {badge.label}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3 text-[10px] font-mono text-slate-600">
                          subs:{refs.active_subscriptions ?? 0}
                          {' · '}tx:{refs.historical_transactions ?? 0}
                          {' · '}quota:{refs.quota_state_rows ?? 0}
                          {' · '}fin:{refs.financial_contracts ?? 0}
                        </td>
                        <td className="py-2 pr-3 text-slate-600 max-w-xs">{item.retention_reason}</td>
                        <td className="py-2 text-right whitespace-nowrap">
                          {item.status === 'active' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-[10px] mr-1"
                              disabled={busyId === item.id}
                              onClick={() => handleDeactivate(item)}
                              data-testid={`cleanup-deactivate-${item.id}`}
                            >
                              Deactivate
                            </Button>
                          )}
                          <Button
                            variant={item.cleanup_eligible ? 'destructive' : 'outline'}
                            size="sm"
                            className="h-7 text-[10px]"
                            disabled={!item.cleanup_eligible || busyId === item.id}
                            onClick={() => setConfirmDelete({ id: item.id, name: item.name_en, key: item.package_key })}
                            title={item.cleanup_eligible ? 'Safe delete via canonical writer' : 'Blocked by dependencies'}
                            data-testid={`cleanup-delete-${item.id}`}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {data?.canonical_writers && (
            <p className="text-[10px] text-slate-400 mt-3" data-testid="cleanup-writer-attribution">
              Canonical writers: <code>{data.canonical_writers.deactivate}</code> · <code>{data.canonical_writers.delete}</code>
            </p>
          )}
        </CardContent>
      </Card>

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="cleanup-delete-modal"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-slate-900">Confirm canonical delete</h3>
            <p className="text-sm text-slate-600">
              This will call the canonical <code>DELETE /api/packages/{'{id}'}</code> writer
              for <span className="font-mono">{confirmDelete.name || confirmDelete.key}</span>.
              The writer will refuse with HTTP 409 if any dependency reference appeared since the
              inventory was loaded.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setConfirmDelete(null)} data-testid="cleanup-delete-cancel">
                Cancel
              </Button>
              <Button variant="destructive" size="sm" onClick={handleConfirmDelete} data-testid="cleanup-delete-confirm">
                Delete via canonical writer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stable list of valid tab keys. Diagnostics and Cleanup Inventory are
// only valid (and only rendered) when the user is a TreatX super admin.
const TABS_BASE = ['catalog'];
const TABS_SUPER_ADMIN = ['catalog', 'diagnostics', 'cleanup'];

export default function PackagesAdminMerged() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { user } = getUnifiedAuth();
  const isSuperAdmin = !!user && SUPER_ADMIN_ROLES.includes(user.role);
  const validTabs = isSuperAdmin ? TABS_SUPER_ADMIN : TABS_BASE;

  const initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    validTabs.includes(initialTab) ? initialTab : 'catalog',
  );

  const handleTabChange = (v) => {
    if (!validTabs.includes(v)) return;
    setActiveTab(v);
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', v);
    setSearchParams(sp, { replace: true });
  };
  useEffect(() => {
    const t = searchParams.get('tab');
    if (validTabs.includes(t) && t !== activeTab) setActiveTab(t);
  }, [searchParams, activeTab, validTabs]);

  const { getAllPackages, lastSync } = usePackageCatalogOptional();
  const allPackages = useMemo(() => getAllPackages() || [], [getAllPackages]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="packages-admin-merged">
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/treatx-admin/dashboard')}
                data-testid="packages-admin-back-btn"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0">
                <h1
                  className="text-2xl font-bold tracking-tight flex items-center gap-2"
                  data-testid="packages-admin-title"
                >
                  <Package className="h-6 w-6 text-purple-600" />
                  Packages
                </h1>
                <p className="text-sm text-muted-foreground">
                  One canonical package editor. Internal tools live under super-admin tabs.
                </p>
              </div>
            </div>
            <Badge variant="outline" className="text-xs">
              {allPackages.length} packages in catalog
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <div className="overflow-x-auto -mx-1 px-1 mb-4">
            <TabsList data-testid="packages-tabs-list" className="inline-flex w-max">
              <TabsTrigger value="catalog" data-testid="packages-tab-catalog">
                <Package className="h-3.5 w-3.5 mr-1.5" /> Package Catalog
              </TabsTrigger>
              {isSuperAdmin && (
                <TabsTrigger value="diagnostics" data-testid="packages-tab-diagnostics">
                  <FileJson className="h-3.5 w-3.5 mr-1.5" /> Diagnostics (internal · read-only)
                </TabsTrigger>
              )}
              {isSuperAdmin && (
                <TabsTrigger value="cleanup" data-testid="packages-tab-cleanup">
                  <Wrench className="h-3.5 w-3.5 mr-1.5" /> Cleanup Inventory (internal)
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <TabsContent
            value="catalog"
            data-testid="packages-tab-catalog-content"
            className="overflow-x-auto"
          >
            <TabErrorBoundary>
              <ClinicCRMPackageManagementPage embedded />
            </TabErrorBoundary>
          </TabsContent>

          {isSuperAdmin && (
            <TabsContent value="diagnostics" data-testid="packages-tab-diagnostics-content">
              <TabErrorBoundary>
                <DiagnosticsPanel allPackages={allPackages} lastSync={lastSync} />
              </TabErrorBoundary>
            </TabsContent>
          )}

          {isSuperAdmin && (
            <TabsContent value="cleanup" data-testid="packages-tab-cleanup-content">
              <TabErrorBoundary>
                <CleanupInventoryPanel />
              </TabErrorBoundary>
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
